<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onDestroy, onMount } from 'svelte';
	import type { Device } from 'src/types/Device';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import {
		Alert,
		AlertV2,
		Button,
		ButtonV2,
		Modal,
		Text,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
	} from '@Shelly/shelly-design-system/index';
	import { persistLiquidLevelIntroductionSeen } from 'src/helpers/blu_liquid_level_sensor/blu_liquid_level_sensor_helpers';
	import { getLiquidLevelIntroWizardSteps } from 'src/helpers/blu_liquid_level_sensor/liquid_level_intro_wizard';
	import { mobile } from 'src/services/mobile-rework';

	export let device: Device;
	export let showModal = false;

	$: ButtonComp = $showNewLayout ? ButtonV2 : Button;

	const createWizardItems = () =>
		getLiquidLevelIntroWizardSteps().map((step, index) => ({
			...step,
			active: index === 0,
			disabled: false,
			showMoreDots: false,
			activeIndex: index,
		}));

	let wizardItems = createWizardItems();
	let activeStep = wizardItems[0];

	const resetWizard = () => {
		wizardItems = createWizardItems();
		activeStep = wizardItems[0];
	};

	const onClickWizardItem = (_event: Event | null, index: number) => {
		wizardItems = wizardItems.map((item, i) => {
			if (i === index) {
				activeStep = {
					...item,
					activeIndex: i,
				};

				return {
					...item,
					active: true,
				};
			}

			return {
				...item,
				active: false,
			};
		});
	};

	const onCloseModal = async () => {
		if (!showModal) {
			return;
		}

		showModal = false;
		resetWizard();
		await persistLiquidLevelIntroductionSeen(device);
	};

	const handleBackButton = () => {
		void onCloseModal();
	};

	onMount(() => {
		if (!mobile.isMobile) {
			return;
		}

		mobile.addEventListener('back_button', handleBackButton);
		return () => mobile.removeEventListener('back_button', handleBackButton);
	});

	onDestroy(() => {
		if (showModal) {
			void persistLiquidLevelIntroductionSeen(device);
		}
	});
</script>

<Modal
	on:close={() => {
		void onCloseModal();
	}}
	{showModal}
	title={$format('title_introduction_wizard', { default: 'Introduction wizard' })}
	backdropClose={false}
>
	<Wizard slot="toolbar" class="intro-wizard-steps">
		{#each wizardItems as item, i (item.key)}
			<WizardItem
				on:click={(event) => {
					onClickWizardItem(event, i);
				}}
				active={item.active}
				title={$format(item.tabTitleKey, { default: item.tabTitle })}
				tooltipTitle={$format(item.titleKey, { default: item.title })}
				disabled={item.disabled}
				showMoreDots={item.showMoreDots}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<div slot="content">
		{#if showModal}
			<WizardContainer activeStepIndex={activeStep.activeIndex} adaptiveHeight>
				{#each wizardItems as item, i (item.key)}
					<WizardContent active={activeStep.activeIndex === i}>
						<div class="width-full d-flex flex-direction-column">
							<Text styleType="h2" class="mb-s">
								{$format(item.subtitleKey, { default: item.subtitle })}
							</Text>
							<hr class="mb-s" />
							{#each item.paragraphs as paragraph (paragraph.key)}
								<Text styleType="body-small" class="mb-s">
									{$format(paragraph.key, { default: paragraph.default })}
								</Text>
							{/each}
							{#if item.key === 'calibrate'}
								{#if $showNewLayout}
									<AlertV2
										heading={$format('alert_heading_liquid_level_intro_calibrate', {
											default: 'Keep hands off the sensor',
										})}
										message={$format('alert_message_liquid_level_intro_calibrate', {
											default:
												'Trigger Empty or Full, then step away so your hands do not affect the reading.',
										})}
									/>
								{:else}
									<Alert
										type="info"
										heading={$format('alert_heading_liquid_level_intro_calibrate', {
											default: 'Keep hands off the sensor',
										})}
										message={$format('alert_message_liquid_level_intro_calibrate', {
											default:
												'Trigger Empty or Full, then step away so your hands do not affect the reading.',
										})}
									/>
								{/if}
							{/if}
						</div>
					</WizardContent>
				{/each}
			</WizardContainer>
		{/if}
	</div>

	<div slot="footer" class="d-flex justify-content-end">
		{#if activeStep.activeIndex > 0}
			<svelte:component
				this={ButtonComp}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				variant="secondary"
				size="m"
				responsiveSize={{
					sm: 'm',
				}}
				aria-label={$format('button_back', { default: 'Back' })}
				on:click={() => {
					onClickWizardItem(null, activeStep.activeIndex - 1);
				}}
			>
				{$format('button_back', { default: 'Back' })}
			</svelte:component>
		{/if}
		<svelte:component
			this={ButtonComp}
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			size="m"
			responsiveSize={{
				sm: 'm',
			}}
			aria-label={activeStep.activeIndex < wizardItems.length - 1
				? $format('button_next', { default: 'Next' })
				: $format('button_got_it', { default: 'Okay, got it!' })}
			on:click={() => {
				if (activeStep.activeIndex < wizardItems.length - 1) {
					onClickWizardItem(null, activeStep.activeIndex + 1);
				} else {
					void onCloseModal();
				}
			}}
		>
			{#if activeStep.activeIndex < wizardItems.length - 1}
				{$format('button_next', { default: 'Next' })}
			{:else}
				{$format('button_got_it', { default: 'Okay, got it!' })}
			{/if}
		</svelte:component>
	</div>
</Modal>

<style>
	.intro-wizard-steps {
		min-width: 0;
		width: 100%;
		overflow-x: auto;
	}
</style>
